import { types as api } from '@mesa-labs/mesa-api';
import { StatusLabel } from '@mesa-labs/mesa-ui';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  DetailSection, ExpandedSectionTitle, SectionHeader, SectionLink,
} from './ExpandedRowSection';

// A vendor's Enablement V2 steps and statuses: shared by the Enablement expanded row and the Action Item drawer.

// A former standalone card (Vendor Actions, ...) as a section of the merged status card.
const StatusSubsection = styled.div`
  & + & {
    margin-top: 12px;
  }
`;

const StepNumber = styled.span`
  color: #98A2B3;
  font-size: 12px;
  min-width: 18px;
  text-align: right;
  flex-shrink: 0;
`;

export const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #F2F4F7;
  }
`;

export const DetailLabel = styled.span`
  color: #667085;
  font-size: 12px;
`;

const DocLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DocIconLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: #7B5EA7;
  opacity: 0.7;
  line-height: 1;
  &:hover { opacity: 1; }
`;

// ─── Status color helpers ─────────────────────────────────────────────────────

export const statusColorKey = (status: string): string => {
  switch (status) {
    case 'Complete':
    case 'Completed':
    case 'Passed':
    case 'Approved':
      return 'succeeded';
    case 'Incomplete':
    case 'Failed':
      return 'failed';
    case 'Pending':
      return 'pending';
    case 'Needs Review':
    case 'Flagged':
    case 'Requires Review':
      return 'warning';
    case 'Missing':
      return 'failed';
    case 'Not Required':
      return 'neutral';
    default:
      return status.toLowerCase();
  }
};

const vendorActionsDetailStatusColorKey = (d: api.EnablementV2DetailItem): string => {
  if (d.label === 'In-Flights' && d.status === api.EnablementV2StepStatus.Complete) {
    return 'recommended';
  }
  return statusColorKey(d.status);
};

export function StepsCard({ steps }: { steps: readonly api.EnablementV2StepItem[] }): React.ReactElement {
  return (
    <DetailSection>
      <SectionHeader><ExpandedSectionTitle>Steps</ExpandedSectionTitle></SectionHeader>
      {steps.map((step, i) => (
        <DetailRow key={step.step}>
          <DocLabelWrapper>
            <StepNumber>{i + 1}.</StepNumber>
            <DetailLabel>{step.step}</DetailLabel>
          </DocLabelWrapper>
          <StatusLabel status={statusColorKey(step.status)} label={step.status} />
        </DetailRow>
      ))}
    </DetailSection>
  );
}

type VendorStatusRow = Pick<api.VendorStatusSummaryItem, 'vendorId' | 'vendorActionsDetail' | 'assignmentUwDetail' | 'autoChecksDetail'>;

// Vendor Actions, Assignment & UW and Auto Checks as one card.
export function VendorStatusCard({ row }: { row: VendorStatusRow }): React.ReactElement {
  return (
    <DetailSection>
      <StatusSubsection>
        <SectionHeader>
          <ExpandedSectionTitle>Vendor Actions</ExpandedSectionTitle>
          <SectionLink to={`/vendors/${row.vendorId}/verification-documents`}>View Documents</SectionLink>
        </SectionHeader>
        {row.vendorActionsDetail.map((d: api.EnablementV2DetailItem) => (
          <DetailRow key={d.label}>
            <DocLabelWrapper>
              <DetailLabel>{d.label}</DetailLabel>
              {d.documentId && (
                <DocIconLink to={`/vendors/${row.vendorId}/documents/${d.documentId}`} title="View document">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </DocIconLink>
              )}
            </DocLabelWrapper>
            <StatusLabel status={vendorActionsDetailStatusColorKey(d)} label={d.status} />
          </DetailRow>
        ))}
      </StatusSubsection>
      <StatusSubsection>
        <SectionHeader>
          <ExpandedSectionTitle>Assignment &amp; UW</ExpandedSectionTitle>
          <SectionLink to={`/vendors/${row.vendorId}/underwriting`}>View Underwriting</SectionLink>
        </SectionHeader>
        {row.assignmentUwDetail.map((d: api.EnablementV2DetailItem) => (
          <DetailRow key={d.label}>
            <DetailLabel>{d.label}</DetailLabel>
            <StatusLabel status={statusColorKey(d.status)} label={d.status} />
          </DetailRow>
        ))}
      </StatusSubsection>
      <StatusSubsection>
        <SectionHeader><ExpandedSectionTitle>Auto Checks</ExpandedSectionTitle></SectionHeader>
        {row.autoChecksDetail.map((d: api.EnablementV2DetailItem) => (
          <DetailRow key={d.label}>
            <DocLabelWrapper>
              <DetailLabel>{d.label}</DetailLabel>
              {d.documentId && (
                <DocIconLink to={`/vendors/${row.vendorId}/documents/${d.documentId}`} title="View document">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </DocIconLink>
              )}
            </DocLabelWrapper>
            <StatusLabel status={statusColorKey(d.status)} label={d.status} />
          </DetailRow>
        ))}
      </StatusSubsection>
    </DetailSection>
  );
}
